import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { catchError, firstValueFrom, map, Observable, of, switchMap, tap, throwError } from 'rxjs';
import { environment } from 'src/environments/environment';
import { AuthUtils } from './auth/auth.utils';
import { UserAuthService } from './auth/userAuth.service';
import { jwtDecode } from 'jwt-decode';
import { Router } from '@angular/router';
import { HospitalStateService } from './services/hospitalstate.service';
import { IpdFeatureService } from './services/ipd-feature.service';

@Injectable({ providedIn: 'root' })
export class LoginService {
  private _authenticated: boolean = false;
  private _isLoggingOut: boolean = false;

  get isLoggingOut(): boolean {
    return this._isLoggingOut;
  }

  constructor(
    private _httpclient: HttpClient,
    private _userService: UserAuthService,
    private _router: Router,
    private _hospital: HospitalStateService,
    private _ipdFeature: IpdFeatureService
  ) {
    const token = this.accessToken;
    if (token && !AuthUtils.isTokenExpired(token)) {
      this._authenticated = true;
    }
  }

  set accessToken(token: string) {
    localStorage.setItem('accessToken', token);
  }

  get accessToken(): string {
    return localStorage.getItem('accessToken') ?? '';
  }

  private getDecodedToken(): any | null {
    const token = localStorage.getItem('accessToken');
    if (!token) return null;
    try {
      return jwtDecode(token);
    } catch (error) {
      console.error("Error decoding token:", error);
      return null;
    }
  }

  signIn(credentials: { username: string; password: string }): Observable<any> {
    // Throw error, if the user is already logged in
    if (this._authenticated) {
      this._router.navigate(['/dashboard']);
      // return throwError('User is already logged in.');
    }

    return this._httpclient.post(`${environment.baseURl}/Auth/login`, credentials).pipe(
      switchMap((response: any) => {
        // Store the access token in the local storage
        // api repsonse.result.token
        this.accessToken = response.result.token;

        // Set the authenticated flag to true
        this._authenticated = true;

        // Store the user on the user service

        this._userService.user = response.result.userName;

        //local strorage store id of user
        localStorage.setItem("userid", response.result.id);
        const hospitalId = this.getHospitalId() as number;
        return this._userService.getHospital(hospitalId).pipe(
          tap(res => {
            this._hospital.setHospital(res.result);
          }),
          map(() => response)
        );
      })
    );
  }

  signOut(): Observable<any> {
    // Prevent multiple logout calls
    if (this._isLoggingOut) {
      return of(true);
    }
    this._isLoggingOut = true;

    // Clear hospital state before navigation
    this._hospital.clearHospital();

    // Remove the access token from the local storage
    localStorage.removeItem('accessToken');
    localStorage.removeItem('userid');

    // Set the authenticated flag to false
    this._authenticated = false;

    // Navigate to login page after state is cleared
    this._router.navigate(['authentication/login']).then(() => {
      this._isLoggingOut = false;
    });

    return of(true);
  }

  isIPDTabShown(): boolean {
    return this._ipdFeature.isEnabled;
  }

  initializeAuth() {
    const token = this.accessToken;
    if (token && !AuthUtils.isTokenExpired(token)) {
      const hospitalId = this.getHospitalId();
      if (hospitalId) {
        return firstValueFrom(this._userService.getHospital(hospitalId).pipe(
          tap(res => {
            this._hospital.setHospital(res.result);
          }),
          catchError(() => of(null))
        ));
      }
    }
    return Promise.resolve();
  }

  getHospitalFromToken(): string[] | null {
    const token = localStorage.getItem("accessToken");
    if (!token) return null;

    try {
      const decodedToken: any = jwtDecode(token);
      return decodedToken?.["http://schemas.xmlsoap.org/ws/2009/09/identity/claims/actor"] || [];
    } catch (error) {
      console.error("Error decoding token:", error);
      return null;
    }
  }

  getHospitalId(): number | null {
    const decoded = this.getDecodedToken();
    if (!decoded) return null;
    const hospitalIdClaim = decoded?.["http://schemas.xmlsoap.org/ws/2009/09/identity/claims/actor"];
    return hospitalIdClaim ? Number(hospitalIdClaim) : null;
  }

  getUsrRoleFromToken(): string[] | null {
    const decoded = this.getDecodedToken();
    if (!decoded) return null;
    return decoded?.["http://schemas.microsoft.com/ws/2008/06/identity/claims/role"] || [];
  }

  isDoctor(): boolean {
    const roles = this.getUsrRoleFromToken();
    return roles?.includes("Doctor") ?? false;
  }

  getUserId(): number | null {
    const decoded = this.getDecodedToken();
    if (!decoded) return null;
    return decoded?.["http://schemas.xmlsoap.org/ws/2005/05/identity/claims/nameidentifier"]
      ? Number(decoded["http://schemas.xmlsoap.org/ws/2005/05/identity/claims/nameidentifier"])
      : null;
  }
}
